import { IconifyIcon } from "@iconify/react";
import { Box, Collapse, GlobalStyles } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { SnackbarKey, SnackbarProvider } from "notistack";
import { ReactNode, useRef } from "react";
import ErrorIllustration from "src/assets/illustrations/ErrorIllustrations";
import { ReactComponent as Success } from "../assets/illustrations/success.svg";
import useSettings from "../hooks/useSettings";
import { ColorSchema } from "../theme/palette";
import Iconify from "./Iconify";
import { IconButtonAnimate } from "./animate";
function SnackbarStyles() {
  const theme = useTheme();

  const isLight = theme.palette.mode === "light";

  return (
    <GlobalStyles
      styles={{
        "#root": {
          "& .SnackbarContent-root": {
            width: "100%",
            maxWidth: "80vw",
            padding: theme.spacing(1),
            margin: theme.spacing(0.25, 0),
            boxShadow: theme.customShadows.z8,
            borderRadius: theme.shape.borderRadius,
            color: theme.palette.grey[isLight ? 0 : 800],
            border: `solid 1px ${theme.palette.grey[isLight ? 300 : 700]}`,
            backgroundColor: theme.palette.grey[isLight ? 900 : 0],
            "&.SnackbarItem-variantSuccess, &.SnackbarItem-variantError, &.SnackbarItem-variantWarning, &.SnackbarItem-variantInfo":
            {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.paper,
            },
            [theme.breakpoints.up("md")]: {
              minWidth: 240,
            },
          },
          "& .SnackbarItem-message": {
            padding: "0 !important",
            fontWeight: theme.typography.fontWeightMedium,
            whiteSpace: "pre-line",
          },
          "& .SnackbarItem-action": {
            marginRight: 0,
            color: theme.palette.action.active,
            "& svg": { width: 20, height: 20 },
          },
          "&.SnackbarItem-contentRoot.desigual-error-snackbar, & .SnackbarItem-contentRoot.desigual-error-snackbar":
            {
              alignItems: "center",
              flexWrap: "nowrap",
              "& .SnackbarItem-message": {
                alignItems: "center",
              },
              "& .SnackbarItem-message > svg, & .SnackbarItem-message > .snackbar-message-icon":
                {
                  flexShrink: 0,
                },
              "& .SnackbarItem-action": {
                flexShrink: 0,
                alignSelf: "center",
                paddingLeft: theme.spacing(1),
              },
            },
          "&.SnackbarItem-contentRoot.desigual-error-snackbar--multiline, & .SnackbarItem-contentRoot.desigual-error-snackbar--multiline":
            {
              alignItems: "flex-start",
              "& .SnackbarItem-message": {
                alignItems: "flex-start",
              },
              "& .SnackbarItem-action": {
                alignSelf: "flex-start",
                marginTop: theme.spacing(0.25),
              },
            },
        },
      }}
    />
  );
}

// ----------------------------------------------------------------------

type Props = {
  children: ReactNode;
};

export default function NotistackProvider({ children }: Props) {
  const { themeDirection } = useSettings();

  const isRTL = themeDirection === "rtl";

  const notistackRef = useRef<any>(null);

  const onClose = (key: SnackbarKey) => () => {
    notistackRef.current.closeSnackbar(key);
  };
  return (
    <>
      <SnackbarStyles />

      <SnackbarProvider
        ref={notistackRef}
        dense
        maxSnack={5}
        preventDuplicate
        autoHideDuration={3000}
        TransitionComponent={isRTL ? Collapse : undefined}
        variant="success" // Set default variant
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        iconVariant={{
          info: <SnackbarIcon icon={"eva:info-fill"} color="info" />,
          success: <Success />,
          warning: (
            <SnackbarIcon icon={"eva:alert-triangle-fill"} color="warning" />
          ),
          error: <ErrorIllustration />,
        }}
        action={(key) => (
          <IconButtonAnimate
            size="small"
            onClick={onClose(key)}
            sx={{ p: 0.5 }}
          >
            <Iconify icon={"eva:close-fill"} />
          </IconButtonAnimate>
        )}
      >
        {children}
      </SnackbarProvider>
    </>
  );
}

// ----------------------------------------------------------------------

type SnackbarIconProps = {
  icon: IconifyIcon | string;
  color: ColorSchema;
};

function SnackbarIcon({ icon, color }: SnackbarIconProps) {
  return (
    <Box
      component="span"
      className="snackbar-message-icon"
      sx={{
        mr: 1.5,
        width: 40,
        height: 40,
        display: "flex",
        borderRadius: 1.5,
        alignItems: "center",
        justifyContent: "center",
        color: `${color}.main`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.16),
      }}
    >
      <Iconify icon={icon} width={24} height={24} />
    </Box>
  );
}
